import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import AddCommentIcon from '@mui/icons-material/AddComment';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import { useTranslation } from 'react-i18next';
import {
  useCreateFeedback,
  useDeleteFeedback,
  useResolveFeedback,
  useUpdateFeedback
} from '../../hooks/useScriptReview';
import type {
  Feedback,
  FeedbackCategory,
  FeedbackSeverity,
  FeedbackStatus,
  Script
} from '../../types';

type FeedbackTabProps = {
  masterScript: Script;
  children: Script[];
  currentUser?: string;
};

const SEVERITY_COLOR: Record<FeedbackSeverity, 'info' | 'warning' | 'error'> = {
  info: 'info',
  warning: 'warning',
  blocker: 'error'
};

const STATUS_COLOR: Record<FeedbackStatus, 'default' | 'success' | 'warning'> = {
  open: 'warning',
  resolved: 'success',
  wontfix: 'default'
};

const CATEGORIES: FeedbackCategory[] = [
  'general',
  'command',
  'test-point',
  'tolerance',
  'calibrator',
  'equipment'
];

const SEVERITIES: FeedbackSeverity[] = ['info', 'warning', 'blocker'];

const parseFeedback = (scriptData: string | null): Feedback[] => {
  if (!scriptData) return [];
  try {
    const parsed = JSON.parse(scriptData);
    return Array.isArray(parsed?.feedback) ? (parsed.feedback as Feedback[]) : [];
  } catch {
    return [];
  }
};

type FormState = {
  author: string;
  category: string;
  severity: FeedbackSeverity;
  comment: string;
  applyToItemGlobalId: string;
  applyToStepPath: string;
};

const initialFormState = (currentUser?: string): FormState => ({
  author: currentUser ?? '',
  category: 'general',
  severity: 'warning',
  comment: '',
  applyToItemGlobalId: '',
  applyToStepPath: ''
});

export const FeedbackTab = ({
  masterScript,
  children,
  currentUser
}: FeedbackTabProps) => {
  const { t } = useTranslation();
  const feedback = useMemo(
    () => parseFeedback(masterScript.scriptData),
    [masterScript.scriptData]
  );

  const [editing, setEditing] = useState<Feedback | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState<FormState>(initialFormState(currentUser));

  const [resolveTarget, setResolveTarget] = useState<Feedback | null>(null);
  const [resolveStatus, setResolveStatus] = useState<FeedbackStatus>('resolved');
  const [resolveComment, setResolveComment] = useState('');

  const createFeedback = useCreateFeedback(masterScript.id);
  const updateFeedback = useUpdateFeedback(masterScript.id);
  const resolveFeedback = useResolveFeedback(masterScript.id);
  const deleteFeedback = useDeleteFeedback(masterScript.id);

  const isFormSubmitting = createFeedback.isPending || updateFeedback.isPending;

  const childOptions = useMemo(
    () => children.map(c => ({ id: c.id, globalId: c.globalId })),
    [children]
  );

  const openAdd = () => {
    setEditing(null);
    setForm(initialFormState(currentUser));
    setShowAdd(true);
  };

  const openEdit = (item: Feedback) => {
    setEditing(item);
    setForm({
      author: item.author,
      category: item.category,
      severity: item.severity,
      comment: item.comment,
      applyToItemGlobalId: item.applyTo?.itemGlobalId ?? '',
      applyToStepPath: item.applyTo?.stepPath ?? ''
    });
    setShowAdd(true);
  };

  const closeForm = () => {
    setShowAdd(false);
    setEditing(null);
  };

  const handleSubmitForm = () => {
    if (!form.author.trim() || !form.comment.trim()) return;
    const applyTo = form.applyToItemGlobalId
      ? {
          itemGlobalId: form.applyToItemGlobalId,
          stepPath: form.applyToStepPath || null
        }
      : null;
    const body = {
      author: form.author.trim(),
      category: form.category,
      severity: form.severity,
      comment: form.comment.trim(),
      applyTo
    };

    if (editing) {
      updateFeedback.mutate(
        { feedbackId: editing.id, body },
        { onSuccess: closeForm }
      );
    } else {
      createFeedback.mutate(body, { onSuccess: closeForm });
    }
  };

  const openResolve = (item: Feedback) => {
    setResolveTarget(item);
    setResolveStatus('resolved');
    setResolveComment('');
  };

  const closeResolve = () => setResolveTarget(null);

  const handleSubmitResolve = () => {
    if (!resolveTarget) return;
    resolveFeedback.mutate(
      {
        feedbackId: resolveTarget.id,
        body: {
          status: resolveStatus,
          comment: resolveComment || null,
          resolvedBy: currentUser ?? null
        }
      },
      { onSuccess: closeResolve }
    );
  };

  const handleDelete = (item: Feedback) => {
    if (!confirm(`Delete feedback "${item.comment.slice(0, 60)}…"?`)) return;
    deleteFeedback.mutate(item.id);
  };

  return (
    <Paper elevation={1} sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 2,
          flexWrap: 'wrap'
        }}
      >
        <RateReviewOutlinedIcon color="primary" />
        <Typography variant="h6" sx={{ flex: 1 }}>
          {t('scripts.metrologistFeedback', 'Metrologist Feedback')}
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddCommentIcon />}
          onClick={openAdd}
        >
          Add feedback
        </Button>
      </Box>

      {feedback.length === 0 ? (
        <Typography color="text.secondary">
          {t(
            'scripts.feedbackEmpty',
            'No feedback yet. Add a note from a metrologist review.'
          )}
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 110 }}>Severity</TableCell>
                <TableCell sx={{ width: 130 }}>Category</TableCell>
                <TableCell>Comment</TableCell>
                <TableCell sx={{ width: 130 }}>Author</TableCell>
                <TableCell sx={{ width: 110 }}>Status</TableCell>
                <TableCell sx={{ width: 170 }} align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {feedback.map(item => (
                <TableRow key={item.id} hover>
                  <TableCell>
                    <Chip
                      size="small"
                      label={item.severity}
                      color={SEVERITY_COLOR[item.severity] ?? 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {item.category}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{item.comment}</Typography>
                    {item.applyTo?.itemGlobalId && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 0.5 }}
                      >
                        On: {item.applyTo.itemGlobalId}
                        {item.applyTo.stepPath ? ` · ${item.applyTo.stepPath}` : ''}
                      </Typography>
                    )}
                    {item.resolution?.comment && (
                      <Typography
                        variant="caption"
                        color="success.main"
                        sx={{ display: 'block', mt: 0.5 }}
                      >
                        Resolution: {item.resolution.comment}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{item.author}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(item.createdAt).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={item.status}
                      color={STATUS_COLOR[item.status] ?? 'default'}
                      variant={item.status === 'open' ? 'outlined' : 'filled'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      {item.status === 'open' && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => openResolve(item)}
                        >
                          Resolve
                        </Button>
                      )}
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit(item)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(item)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={showAdd} onClose={closeForm} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? 'Edit feedback' : 'Add feedback'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Author"
              value={form.author}
              onChange={e => setForm({ ...form, author: e.target.value })}
              fullWidth
              required
            />
            <Stack direction="row" spacing={2}>
              <TextField
                select
                label="Category"
                value={form.category}
                onChange={e => setForm({ ...form, category: e.target.value })}
                sx={{ flex: 1 }}
              >
                {CATEGORIES.map(c => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Severity"
                value={form.severity}
                onChange={e =>
                  setForm({
                    ...form,
                    severity: e.target.value as FeedbackSeverity
                  })
                }
                sx={{ flex: 1 }}
              >
                {SEVERITIES.map(s => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
            <TextField
              label="Comment"
              value={form.comment}
              onChange={e => setForm({ ...form, comment: e.target.value })}
              fullWidth
              required
              multiline
              minRows={3}
            />
            <TextField
              select
              label="Applies to (optional)"
              value={form.applyToItemGlobalId}
              onChange={e =>
                setForm({ ...form, applyToItemGlobalId: e.target.value })
              }
              fullWidth
              helperText="Pick the item this feedback is about, or leave blank for general."
            >
              <MenuItem value="">— None (script-level) —</MenuItem>
              {childOptions.map(c => (
                <MenuItem key={c.id} value={c.globalId}>
                  {c.globalId}
                </MenuItem>
              ))}
            </TextField>
            {form.applyToItemGlobalId && (
              <TextField
                label="Step path (optional)"
                value={form.applyToStepPath}
                onChange={e =>
                  setForm({ ...form, applyToStepPath: e.target.value })
                }
                fullWidth
                helperText='e.g., "calibrationSteps[2]"'
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeForm} disabled={isFormSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitForm}
            disabled={
              !form.author.trim() ||
              !form.comment.trim() ||
              isFormSubmitting
            }
          >
            {isFormSubmitting
              ? 'Saving…'
              : editing
                ? 'Save changes'
                : 'Add feedback'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!resolveTarget}
        onClose={closeResolve}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Resolve feedback</DialogTitle>
        <DialogContent>
          {resolveTarget && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {resolveTarget.comment}
              </Typography>
              <TextField
                select
                label="Status"
                value={resolveStatus}
                onChange={e =>
                  setResolveStatus(e.target.value as FeedbackStatus)
                }
                fullWidth
              >
                <MenuItem value="resolved">Resolved</MenuItem>
                <MenuItem value="wontfix">Won't fix</MenuItem>
              </TextField>
              <TextField
                label="Resolution comment (optional)"
                value={resolveComment}
                onChange={e => setResolveComment(e.target.value)}
                fullWidth
                multiline
                minRows={2}
                helperText="What was changed, or why it's not being fixed."
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeResolve} disabled={resolveFeedback.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitResolve}
            disabled={resolveFeedback.isPending}
          >
            {resolveFeedback.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};
